/**
 * @fileoverview Frontend TypeScript interfaces for the Template & Form Builder.
 * @module ui-admin/types/templateTypes
 *
 * Responsibilities:
 * - Define TemplateBuilderTemplate, TemplateBuilderForm, FormField, and related builder types.
 * - Mirror api/tenant-api/src/types/template.ts with string IDs for frontend.
 *
 * Dependencies: None (pure types).
 * Architecture: Canonical location for template builder types; imported by tenant-web and system-web.
 * Notes: Uses plain string IDs instead of ObjectId for frontend consumption.
 */
// Mirrors api/tenant-api/src/types/template.ts but uses plain
// string IDs instead of ObjectId for frontend consumption.
// Canonical location: @firesquirrel/ui-admin — imported by both
// tenant-web and system-web.
// ============================================================

import type { ReportConfig } from './documentTriggerTypes';

// ---------------------------------------------------------------------------
// Enums / Literals
// ---------------------------------------------------------------------------

export type TemplateType = 'wildland' | 'fireCode' | 'hydrant' | 'complex' | 'communityRisk' | 'custom';

/** Controls who can discover and reuse this template or form */
export type TemplateVisibility = 'tenant' | 'organization' | 'system';

// ---------------------------------------------------------------------------
// AI Metadata — fast-path tag matching for Squirrel discovery
// Generated by GPT-4o-mini on save/publish (~$0.001/call)
// ---------------------------------------------------------------------------
export interface AiMetadata {
  aiDescription?: string;    // One-sentence description of what this record does
  aiTags?: string[];         // e.g. ["ab38", "defensible-space", "wildland", "zone-1"]
  aiSuggestedFor?: string[]; // Templates/forms: scenarios this is suggested for
  aiSynonyms?: string[];     // Lookups: alternate names/terms for this value
  aiGeneratedAt?: string;    // ISO timestamp of last AI generation
  aiClassifiedAt?: string;   // ISO timestamp of last AI classification (violations)
}

export type TemplateStatus = 'draft' | 'published' | 'archived';
export type FormStatus = 'draft' | 'published' | 'archived';
export type FormKind = 'standard' | 'header' | 'module';

// ---------------------------------------------------------------------------
// System Module types — code-rendered forms on the canvas
// ContactRoleConfig, ContactsModuleConfig, ContactEntry, ContactChange,
// PhoneRow, EmailRow are the canonical types from @firesquirrel/core-domain.
// Do NOT redefine them here.
// ---------------------------------------------------------------------------

export type ModuleType = 'contacts' | 'violations';

// Re-export canonical contact module types for consumers of @firesquirrel/ui-admin
export type {
  ContactRoleConfig,
  ContactsModuleConfig,
  ContactEntry,
  ContactChange,
  PhoneRow,
  EmailRow,
} from '@firesquirrel/core-domain';

export interface ViolationsModuleConfig {
  moduleType: 'violations';
  codeSetIds: string[];           // Which code sets are searchable
  allowAiSearch: boolean;         // AI-assisted violation lookup
  showNotes: boolean;             // Show the inspector observation note step
  requireNotes: boolean;          // Inspector must enter a note before submitting
  showRemedy: boolean;            // Show the separate remedy / how-to-fix note step
  showSeverity: boolean;          // Show the severity picker step (normal | high | critical)
  showPhotos: boolean;            // Show the photo attachment step
  requirePhotos: boolean;         // Inspector must attach a photo before submitting
  allowCorrectiveAction: boolean; // Allow post-log editing of the note
}

export type ModuleConfig = import('@firesquirrel/core-domain').ContactsModuleConfig | ViolationsModuleConfig;

export type FieldType =
  | 'toggle' | 'text' | 'textarea' | 'number' | 'measurement'
  | 'dropdown' | 'checkboxGroup' | 'radioGroup'
  | 'camera' | 'date' | 'dateTime' | 'photo' | 'signature'
  | 'location' | 'section' | 'instructions'
  | 'repeatableList' | 'dataBound' | 'inspectors';

// ---------------------------------------------------------------------------
// Data-Bound Widget Types
// ---------------------------------------------------------------------------

export type DataBoundMode = 'readOnly' | 'editable';

export type DataBoundInputType =
  | 'text' | 'textarea' | 'number' | 'toggle'
  | 'dropdown' | 'date' | 'address' | 'phone'
  | 'email' | 'lookup' | 'currency';

export type DataBoundDataSource = 'inspectableEntity' | 'inspectionEvent' | 'cases' | 'inspectionCases';

export interface DataBoundWidgetDefinition {
  widgetId: string;
  label: string;
  dataSource: DataBoundDataSource;
  boundField: string;
  compositeFields?: string[];
  displayFormat: string;
  inputType: DataBoundInputType;
  entityCategoryFilter?: readonly ('parcel' | 'residential' | 'building' | 'hydrant' | 'site' | 'complex')[];
  lookupType?: string;
  requiredModule?: string;
  defaultMode: DataBoundMode;
  forceReadOnly?: boolean;
  widgetCategory: string;
  widgetCategoryOrder: number;
  description?: string;
}

export type Operator = 'equals' | 'notEquals' | 'in' | 'notIn' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'exists';

export type ActionType = 'hideField' | 'showField' | 'hideSection' | 'showSection' | 'hideForm' | 'showForm' | 'setOptions' | 'require' | 'disable' | 'enable';

export type RuleScope = 'template' | 'form';

// ---------------------------------------------------------------------------
// Rule Engine Types
// ---------------------------------------------------------------------------

export type Condition =
  | { field: string; op: Operator; value?: any }
  | { and: Condition[] }
  | { or: Condition[] }
  | { not: Condition };

export type Action =
  | { action: 'hideField'; fieldId: string }
  | { action: 'showField'; fieldId: string }
  | { action: 'hideSection'; sectionId: string }
  | { action: 'showSection'; sectionId: string }
  | { action: 'hideForm'; formId: string }
  | { action: 'showForm'; formId: string }
  | { action: 'setOptions'; fieldId: string; options: any[] }
  | { action: 'require'; fieldId: string; requireNotes?: boolean; requirePhotos?: boolean }
  | { action: 'disable'; fieldId: string }
  | { action: 'enable'; fieldId: string };

export interface Rule {
  id: string;
  name?: string;
  scope: RuleScope;
  when: Condition;
  then: Action[];
}

// ---------------------------------------------------------------------------
// Violation Mapping (per-field)
// ---------------------------------------------------------------------------

/**
 * @contract ViolationMapping
 *
 * Purpose:
 * - Connects a field (or a selected response) to a specific violation in a
 *   vendor code set. Drives violation auto-creation during inspections.
 *
 * Fields:
 * - codeSetId: Vendor code set id (empty string allowed when `bindingMode`
 *   is `'pending-tenant-bind'` — the tenant admin resolves it later).
 * - code: Specific section code (empty string allowed when pending).
 * - title / contentSnippet / codeReference: Optional human-readable display
 *   metadata captured from the local violation index at selection time.
 * - trigger: Condition that fires the violation.
 * - requireNotes / requirePhotos / requireLocation / requireCorrectiveAction:
 *   Runtime inspector UX gates.
 * - allowMultipleInstances: Whether one field can emit multiple violations.
 * - deriveInstancesFromRepeatable: Auto-derive instances from repeatable groups.
 * - bindingMode (Step B.1 gap-closure 2026-05-01): `'bound'` (default) when
 *   the mapping is complete, or `'pending-tenant-bind'` when the template
 *   author marked it as "Violation to be added later". The tenant resolves
 *   the binding in the Bind-Violations walkthrough before publishing.
 * - bindingHint: Optional free-text guidance from the author to the tenant
 *   admin who will resolve the binding (e.g. "Use local amendment 903.2.1").
 *
 * Rules:
 * - When `bindingMode === 'pending-tenant-bind'`, tenant-web Publish is
 *   blocked by `PendingBindingsError` until every mapping is converted to
 *   `'bound'` with a real `codeSetId` + `code`.
 * - `bindingMode` is absent on legacy mappings; treat absence as `'bound'`.
 *
 * Sync:
 * - Persisted as part of the template document in both system (library) and
 *   tenant (import + publish) DBs.
 */
export interface ViolationMapping {
  codeSetId: string;
  code: string;
  title?: string;
  contentSnippet?: string;
  codeReference?: string;
  trigger: Condition;
  requireNotes: boolean;
  requirePhotos: boolean;
  requireLocation?: boolean;
  requireCorrectiveAction?: boolean;
  allowMultipleInstances: boolean;
  deriveInstancesFromRepeatable?: boolean;
  bindingMode?: 'bound' | 'pending-tenant-bind';
  bindingHint?: string;
}

// ---------------------------------------------------------------------------
// Field
// ---------------------------------------------------------------------------

export interface FormField {
  id: string;
  type: FieldType;
  label: string;
  helpText?: string;
  required?: boolean;
  defaultValue?: any;
  naEnabled?: boolean;
  visibility?: 'always' | 'conditional';
  initialVisibility?: 'shown' | 'hidden';
  violationMappings?: ViolationMapping[];
  extras?: Record<string, any>;
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

export interface LayoutSection {
  id: string;
  title?: string;
  description?: string;
  columns: number;
  order: number;
  fieldIds: string[];
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  initialVisibility?: 'shown' | 'hidden';
}

export interface FormLayout {
  columns?: number;
  sections: LayoutSection[];
}

// ---------------------------------------------------------------------------
// Cross-Form Dependency
// ---------------------------------------------------------------------------

export interface CrossFormDependency {
  sourceFieldId: string;
  targetFormId: string;
  targetFieldId?: string;
  actionType: 'hide' | 'show' | 'require' | 'disable';
  condition: Condition;
}

// ---------------------------------------------------------------------------
// Form Display Config (mobile menu bar visual identity)
// ---------------------------------------------------------------------------

export interface FormDisplayConfig {
  icon?: string;          // Lucide kebab-case name, e.g. "trees", "flame"
  color?: string;         // Hex color, e.g. "#16a34a"
  abbreviation?: string;  // 1–3 char short label, e.g. "Z0", "EXT", "ID"
  showPageNumber?: boolean; // Header form: show "Page 1 of N" in mobile
  requireAck?: boolean;     // Header form: require inspector to tap "I understand"
  sortLabel?: string;     // Short name for mobile nav (falls back to form.name)
}

// ---------------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------------

export interface TemplateBuilderForm {
  _id: string;
  name: string;
  description?: string;
  category?: string;

  formKind?: FormKind;
  moduleConfig?: ModuleConfig;

  isFixedForm?: boolean;
  fixedFormSourceId?: string;
  derivedFrom?: FormDerivedFrom;

  reusable?: boolean;
  librarySourceId?: string;
  librarySourceName?: string;
  libraryDiverged?: boolean;

  shared?: boolean;
  sharing?: SharingMetadata;

  /** Library visibility: tenant = private, organization = org-wide, system = all tenants */
  visibility?: TemplateVisibility;

  /** AI metadata for Squirrel discovery — generated on save/publish */
  aiMetadata?: AiMetadata;

  templateType: TemplateType;
  templateTypeName: string;

  version: number;
  status: FormStatus;
  publishedAt?: string;
  publishedBy?: string;

  fields: FormField[];
  layout: FormLayout;

  display?: FormDisplayConfig;
  initialVisibility?: 'shown' | 'hidden';

  rules?: Rule[];
  crossFormDependencies?: CrossFormDependency[];

  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;

  _deleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
}

export interface FormDerivedFrom {
  formId: string;
  formVersion: number;
  sourceDepartmentId?: string;
  isFixedFormCopy: boolean;
  copiedAt: string;
  copiedBy: string;
}

// ---------------------------------------------------------------------------
// Template
// ---------------------------------------------------------------------------

export interface FormRef {
  formId: string;
  formVersion: number;
  order: number;
  optional?: boolean;
  formKind?: FormKind;
  moduleConfig?: ModuleConfig;
}

export interface ReinspectionScheduleEntry {
  reinspectionNumber: number;
  dueDays: number;
  label?: string;
}

export interface TemplateParameters {
  autoCreateReinspectionWhenViolations: boolean;
  reinspectionSchedule?: ReinspectionScheduleEntry[];
  maxReinspections?: number;

  allowExtensions?: boolean;
  allowExceptions?: boolean;
  enableSeverity?: boolean;

  selfCertificationEnabled: boolean;
  selfCertificationRequiresPhotos?: boolean;

  emailOwnerOnComplete: boolean;
  emailInspectorSummary: boolean;

  requireInspectorSignature: boolean;
  requireResponsiblePartySignature: boolean;

  /**
   * When true, the inspector MUST select a responsible-party contact before a
   * visit for this template can be completed. When false/omitted, a responsible
   * party is optional and the property owner is used by default.
   */
  requireResponsibleParty?: boolean;

  allowOfflineMode: boolean;
}

export interface TemplateDerivedFrom {
  templateId: string;
  templateVersion: number;
  sourceDepartmentId: string;
  copiedAt: string;
  copiedBy: string;
}

export interface SharingMetadata {
  sourceType: 'system' | 'organization' | 'department';
  sourceId: string;
  sourceItemId: string;
  sourceVersion: number;
  inheritanceMode: 'linked' | 'forked' | 'independent';
  sharedAt: string;
  forkedAt?: string;
  lastSyncedAt?: string;
  canFork: boolean;
  canPullUpdates: boolean;
}

export interface TemplateBuilderTemplate {
  _id: string;

  name: string;
  description?: string;
  templateType: TemplateType;
  templateTypeName: string;

  version: number;
  status: TemplateStatus;
  publishedAt?: string;
  publishedBy?: string;

  isFixedTemplate?: boolean;
  fixedTemplateSourceId?: string;
  derivedFrom?: TemplateDerivedFrom;

  /** Set when this template was adopted from the system library */
  librarySource?: {
    systemTemplateId: string;
    systemTemplateName: string;
    adoptedAt: string;
    adoptedVersion: number;
  };

  // Detailed sharing/inheritance metadata used by sharingService & SharingStatusPanel
  sharing?: SharingMetadata;

  /** Sharing: whether this template is published to the shared org library */
  shared?: boolean;
  /** Scope of sharing: 'org' = organization-wide, 'system' = platform-wide */
  sharedScope?: 'org' | 'system';

  /** Library visibility: tenant = private, organization = org-wide, system = all tenants */
  visibility?: TemplateVisibility;

  /** AI metadata for Squirrel discovery — generated on save/publish */
  aiMetadata?: AiMetadata;
  sharedAt?: string;
  sharedBy?: string;

  /** When true, this template is eligible for bulk (multi-parcel) inspections.
   *  Items that cannot be populated during bulk (photos, signatures, violations,
   *  data-bound widgets, etc.) are skipped — the template builder shows an
   *  awareness summary listing these items when the flag is toggled on. */
  allowBulkInspection?: boolean;

  formRefs: FormRef[];
  rules?: Rule[];
  parameters: TemplateParameters;

  display?: {
    icon?: string;      // Lucide kebab-case name, e.g. "shield-check"
    iconColor?: string; // Hex color, e.g. "#16a34a"
  };

  templateHeader?: {
    enabled: boolean;
    content: string;    // HTML string
  };

  /** Inspection-template document configuration (v2 unified rule model).
   *  Persisted configs may still be in the legacy shape; always read through
   *  `normalizeReportConfig()` from './documentTriggerTypes'. */
  reportConfig?: ReportConfig;

  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;

  _deleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
}

// ---------------------------------------------------------------------------
// Display Helpers
// ---------------------------------------------------------------------------

export const TEMPLATE_TYPE_LABELS: Record<TemplateType, string> = {
  wildland: 'Wildland',
  fireCode: 'Fire Code',
  hydrant: 'Hydrant',
  complex: 'Complex',
  communityRisk: 'Community Risk',
  custom: 'Custom',
};

export const TEMPLATE_TYPE_ICONS: Record<TemplateType, string> = {
  wildland: 'gi-pine-tree',
  fireCode: 'gi-fire',
  hydrant: 'gi-droplets',
  complex: 'gi-modern-city',
  communityRisk: 'gi-checked-shield',
  custom: 'settings',
};

export const STATUS_VARIANT: Record<TemplateStatus | FormStatus, 'success' | 'warning' | 'neutral'> = {
  draft: 'warning',
  published: 'success',
  archived: 'neutral',
};

export const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  toggle: 'Toggle',
  text: 'Text',
  textarea: 'Text Area',
  number: 'Number',
  measurement: 'Measurement',
  dropdown: 'Dropdown',
  checkboxGroup: 'Checkbox Group',
  radioGroup: 'Radio Group',
  camera: 'Camera',
  date: 'Date',
  dateTime: 'Date & Time',
  photo: 'Photo',
  signature: 'Signature',
  location: 'Location',
  section: 'Section',
  instructions: 'General Information',
  repeatableList: 'Repeatable List',
  dataBound: 'Data Bound',
  inspectors: 'Inspectors',
};

export const FIELD_TYPE_ICONS: Record<FieldType, string> = {
  toggle: 'ToggleLeft',
  text: 'Type',
  textarea: 'AlignLeft',
  number: 'Hash',
  measurement: 'Ruler',
  dropdown: 'ChevronDown',
  checkboxGroup: 'CheckSquare',
  radioGroup: 'Circle',
  camera: 'Camera',
  date: 'Calendar',
  dateTime: 'CalendarClock',
  photo: 'Image',
  signature: 'PenTool',
  location: 'MapPin',
  section: 'LayoutDashboard',
  instructions: 'FileText',
  repeatableList: 'Repeat',
  dataBound: 'Link',
  inspectors: 'Users',
};

export const FIELD_TYPE_GROUPS: { label: string; types: FieldType[] }[] = [
  { label: 'Basic', types: ['toggle', 'text', 'textarea', 'number', 'measurement', 'date', 'dateTime'] },
  { label: 'Selection', types: ['dropdown', 'checkboxGroup', 'radioGroup'] },
  { label: 'Media', types: ['camera', 'photo', 'signature', 'location'] },
  { label: 'Layout', types: ['section', 'instructions'] },
  { label: 'Advanced', types: ['repeatableList', 'dataBound', 'inspectors'] },
];

export const OPERATOR_LABELS: Record<Operator, string> = {
  equals: 'equals',
  notEquals: 'does not equal',
  in: 'is one of',
  notIn: 'is not one of',
  gt: 'is greater than',
  gte: 'is at least',
  lt: 'is less than',
  lte: 'is at most',
  contains: 'contains',
  exists: 'is not empty',
};

// ---------------------------------------------------------------------------
// Violation Code Set — Template Builder Interfaces
// ---------------------------------------------------------------------------

export interface AiSuggestedSeverity {
  level: 'critical' | 'high' | 'medium' | 'low';
  confidence: number;
  reasoning?: string;
}

export interface TemplateBuilderViolation {
  _id: string;
  code: string;
  title: string;
  description: string;
  category: string;
  codeReference?: string;
  contentSnippet?: string;
  chapterOrdinal?: string;
  chapterTitle?: string;
  defaultCorrectionDeadlineDays: number;
  requiresReinspection: boolean;
  isActive: boolean;
  aiSuggestedSeverity?: AiSuggestedSeverity;
  aiTags?: string[];
  aiPriorityScore?: number;
  createdAt: string;
  updatedAt: string;
}

export interface TemplateBuilderViolationCodeSet {
  _id: string;
  name: string;
  description?: string;
  source: 'icc' | 'custom';
  jurisdiction?: string;
  codeYear?: string;
  isActive: boolean;
  activatedAt?: string;
  deactivatedAt?: string;
  deactivatedReason?: string;
  version?: number;
  effectiveDate?: string;
  supersededBy?: string;
  violations: TemplateBuilderViolation[];
  chapterToc?: Array<{
    chapterId: string;
    ordinal: string;
    title: string;
    sectionCount: number;
  }>;
  aiMetadata?: AiMetadata;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  _deleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
}
